import { Box, Center, Flex, Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { InternalLink } from "components/Link";
import { Text12, Text14 } from "components/Text";
import { templatePathByTemplateOrSummary } from "helpers/url/pages/v2/actionPaths";
import { ActionTypeTag } from "design-system";
import { ActionType } from "features/Resources/Shared/models";
import { PathId } from "features/Path/models";
import { RandomEmptyActionState } from "components/EmptyComponents/RandomEmptyState";
import SearchBar from "components/SearchBar";
import ControlledDataTable, { ControlledDataTableProps } from "components/Table/ControlledDataTable";
import { useState, useMemo } from "react";
import AddTemplateToPathRowButton from "features/Path/Components/Button/AddTemplateToPathRowButton";
import { TemplateSummary } from "../models";
import { filterTemplateSummaryByText, primaryTemplateTrigger } from "../helpers";
import { useListSharedTemplatesQuery } from "../hooks";
import { DisplayTemplateRecipients } from "./DisplayTemplateRecipients";

const columnHelper = createColumnHelper<TemplateSummary>();

const buildColumns = (pathId: PathId) => [
  columnHelper.accessor((row) => row, {
    id: "templateType",
    header: "Type",
    cell: (row) => {
      const summary = row.getValue();
      const actionType = summary.templateType.replace(/_template$/, "") as ActionType;
      const meetingType = summary.templateType === "meeting_template" ? summary.template.meetingType : undefined;
      return <ActionTypeTag.IconBadgeWithMeetingType actionType={actionType} meetingType={meetingType} />;
    },
    minSize: 24,
    maxSize: 24,
    size: 24,
    meta: {
      sx: {
        paddingLeft: 2,
        paddingRight: 0,
      },
    },
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.getValue(columnId).templateType?.toLowerCase() ?? "";
      const b = rowB.getValue(columnId).templateType?.toLowerCase() ?? "";
      return a.localeCompare(b);
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    cell: (row) => (
      <Box width="fit-content">
        <InternalLink
          to={templatePathByTemplateOrSummary(row.getValue())}
          textDecoration="none"
          _hover={{ textDecoration: "none" }}
          width="fit-content"
        >
          <Text12 wordBreak="break-word">{row.getValue()?.template?.name}</Text12>
        </InternalLink>
      </Box>
    ),
    maxSize: 100,
    minSize: 100,
    size: 100,
    enableSorting: false,
    meta: {
      sx: {
        paddingLeft: 0,
      },
      headerHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "primaryTrigger",
    header: "Sending",
    cell: (row) => <Text12>{primaryTemplateTrigger(row.getValue())?.data?.descriptor}</Text12>,
    sortingFn: (rowA, rowB, columnId) => {
      const a = primaryTemplateTrigger(rowA.getValue(columnId))?.sortValue || 0;
      const b = primaryTemplateTrigger(rowB.getValue(columnId))?.sortValue || 0;
      return a - b;
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "recipients",
    header: "Recipients",
    cell: (row) => <DisplayTemplateRecipients template={row.getValue()} />,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row, {
    id: "buttons",
    header: "",
    cell: (row) => <AddTemplateToPathRowButton pathId={pathId} templatePrefixId={row.getValue()?.template?.prefixId} />,
  }),
];

interface AddTemplatesToPathByListProps {
  pathId: PathId;
}

export default function AddTemplatesToPathByList({ pathId }: AddTemplatesToPathByListProps) {
  const [searchText, setSearchText] = useState("");

  const { data: templates, isFetching } = useListSharedTemplatesQuery({
    // select: (templateSummaries: TemplateSummary[]) => (templateSummaries || []).filter((templateSummary) => !templateSummary?.template?.pathIds?.includes(pathId)),
    select: (templateSummaries: TemplateSummary[]) =>
      (templateSummaries || []).filter(
        (templateSummary) => !templateSummary?.template?.pathIds?.includes(Number(pathId)),
      ),
  });
  const filteredTemplates = useMemo(() => filterTemplateSummaryByText(templates, searchText), [searchText, templates]);

  if (isFetching) {
    return (
      <Center mt="4">
        <Text14 color="fg.emphasized">Loading...</Text14>
      </Center>
    );
  }

  const tableProps: ControlledDataTableProps<TemplateSummary> = {
    defaultSort: [{ id: "primaryTrigger", desc: false }],
    data: filteredTemplates || [],
    isLoading: isFetching,
    columns: buildColumns(pathId),
    initialPagination: { pageIndex: 0, pageSize: 100 },
    textWrap: true,
    emptyStateComponent: <RandomEmptyActionState />,
  };

  return (
    <Flex direction="column">
      <Box>
        <Stack mt="2">
          <SearchBar
            border="1px solid var(--stroke-vibes, #F1F1F1)"
            minWidth="200px"
            flexShrink="1"
            mr="2"
            name="actionsSearch"
            onInput={setSearchText}
          />
        </Stack>
      </Box>
      <Box overflowX="auto" width="100%" mt="2">
        <ControlledDataTable<TemplateSummary> {...tableProps} mt="4" />
      </Box>
    </Flex>
  );
}
